import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  Slide,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { Button } from '../button/button';
import { SteppedProgress } from '../stepped-progress';

export interface IMultiStepModalStep<TData = Record<string, unknown>> {
  title: string | ReactNode;
  body: (data: TData, actions: IMultiStepActions<TData>) => ReactNode;
  canGoNext?: (data: TData) => boolean;
  canGoBack?: boolean;
  hideBackButton?: boolean;
  hideNextButton?: boolean;
  nextButtonText?: string;
  backButtonText?: string;
  onNext?: (data: TData) => void | Promise<void>;
  onBack?: (data: TData) => void | Promise<void>;
}

export interface IMultiStepActions<TData = Record<string, unknown>> {
  next: () => void;
  back: () => void;
  goToStep: (stepIndex: number) => void;
  updateData: (updates: Partial<TData>) => void;
  getData: () => TData;
  getCurrentStep: () => number;
  getTotalSteps: () => number;
  close: () => void;
}

export interface IMultiStepModal<TData = Record<string, unknown>> {
  title?: string | ReactNode;
  steps: IMultiStepModalStep<TData>[];
  initialData?: TData;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fullScreen?: boolean;
  disableBackdropClick?: boolean;
  onComplete?: (data: TData) => void | Promise<void>;
  onCancel?: (data: TData) => void;
  dialogProps?: Partial<DialogProps>;
  /** Show default stepped progress indicator. Default: true */
  showProgress?: boolean;
  /** Custom progress renderer. Overrides showProgress if provided */
  renderProgress?: (currentStep: number, totalSteps: number) => ReactNode;
  /** Optional error handler for step progression failures */
  onError?: (error: Error, context: 'next' | 'back' | 'complete') => void;
}

interface IMultiStepModalRendererProps<TData = Record<string, unknown>> {
  modal: IMultiStepModal<TData>;
  onClose: () => void;
}

// Context to share data and actions across step components
const MultiStepContext = createContext<IMultiStepActions<any> | null>(null);

export const useMultiStepModal = <TData = Record<string, unknown>,>() => {
  const context = useContext(MultiStepContext);
  if (!context) {
    throw new Error('useMultiStepModal must be used within a MultiStepModal');
  }
  return context as IMultiStepActions<TData>;
};

export default function MultiStepModalRenderer<
  TData = Record<string, unknown>,
>({ modal, onClose }: IMultiStepModalRendererProps<TData>) {
  const [currentStep, setCurrentStep] = useState(0);
  const [data, setData] = useState<TData>(modal.initialData || ({} as TData));
  const [isLoading, setIsLoading] = useState(false);
  const prevStepRef = useRef(currentStep);

  // Update previous step reference after render
  useEffect(() => {
    prevStepRef.current = currentStep;
  }, [currentStep]);

  // Calculate slide direction based on previous step
  const slideDirection: 'left' | 'right' =
    currentStep > prevStepRef.current ? 'left' : 'right';

  const currentStepConfig = modal.steps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === modal.steps.length - 1;

  const updateData = useCallback((updates: Partial<TData>) => {
    setData((prev) => ({ ...prev, ...updates }));
  }, []);

  const getData = useCallback(() => data, [data]);

  const getCurrentStep = useCallback(() => currentStep, [currentStep]);

  const getTotalSteps = useCallback(
    () => modal.steps.length,
    [modal.steps.length],
  );

  const close = useCallback(() => {
    onClose();
  }, [onClose]);

  const goToStep = useCallback(
    (stepIndex: number) => {
      if (stepIndex >= 0 && stepIndex < modal.steps.length) {
        setCurrentStep(stepIndex);
      }
    },
    [modal.steps.length],
  );

  const next = useCallback(async () => {
    setIsLoading(true);
    try {
      if (currentStepConfig.onNext) {
        await currentStepConfig.onNext(data);
      }

      if (isLastStep) {
        if (modal.onComplete) {
          await modal.onComplete(data);
        }
        onClose();
      } else {
        setCurrentStep((prev) => Math.min(prev + 1, modal.steps.length - 1));
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in step progression:', error);
      if (modal.onError) {
        modal.onError(
          error instanceof Error ? error : new Error(String(error)),
          isLastStep ? 'complete' : 'next',
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [currentStepConfig, data, isLastStep, modal, onClose]);

  const back = useCallback(async () => {
    setIsLoading(true);
    try {
      if (currentStepConfig.onBack) {
        await currentStepConfig.onBack(data);
      }
      setCurrentStep((prev) => Math.max(prev - 1, 0));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error going back:', error);
      if (modal.onError) {
        modal.onError(
          error instanceof Error ? error : new Error(String(error)),
          'back',
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [currentStepConfig, data, modal]);

  const handleClose = useCallback(() => {
    if (modal.onCancel) {
      modal.onCancel(data);
    }
    onClose();
  }, [modal, data, onClose]);

  const canGoNext = useMemo(() => {
    if (currentStepConfig.canGoNext) {
      return currentStepConfig.canGoNext(data);
    }
    return true;
  }, [currentStepConfig, data]);

  const canGoBack = useMemo(() => {
    if (currentStepConfig.canGoBack !== undefined) {
      return currentStepConfig.canGoBack;
    }
    return !isFirstStep;
  }, [currentStepConfig, isFirstStep]);

  const actions: IMultiStepActions<TData> = useMemo(
    () => ({
      next,
      back,
      goToStep,
      updateData,
      getData,
      getCurrentStep,
      getTotalSteps,
      close,
    }),
    [
      next,
      back,
      goToStep,
      updateData,
      getData,
      getCurrentStep,
      getTotalSteps,
      close,
    ],
  );

  return (
    <MultiStepContext.Provider value={actions}>
      <Dialog
        open
        onClose={modal.disableBackdropClick ? undefined : handleClose}
        maxWidth={modal.size || 'md'}
        fullWidth
        fullScreen={modal.fullScreen}
        {...modal.dialogProps}
      >
        {modal.title && (
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 0.5,
            }}
          >
            {modal.title}
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
        )}
        {modal.renderProgress ? (
          <Box sx={{ px: 5, py: 1 }}>
            {modal.renderProgress(currentStep + 1, modal.steps.length)}
          </Box>
        ) : modal.showProgress !== false ? (
          <Box sx={{ pt: modal.title ? 0 : 1, px: 1, pb: 1 }}>
            <SteppedProgress
              steps={modal.steps.map((step) =>
                typeof step.title === 'string' ? step.title : 'Step',
              )}
              currentStep={currentStep + 1}
            />
          </Box>
        ) : null}

        <DialogContent
          sx={{
            minHeight: 200,
            overflow: 'scroll',
            position: 'relative',
            py: 1,
          }}
        >
          <Slide
            direction={slideDirection}
            in
            mountOnEnter
            unmountOnExit
            key={currentStep}
          >
            <Box>{currentStepConfig.body(data, actions)}</Box>
          </Slide>
        </DialogContent>

        <DialogActions>
          {!currentStepConfig.hideBackButton && canGoBack && (
            <Button
              content={currentStepConfig.backButtonText || 'Back'}
              variant="ghost"
              onClick={back}
              disabled={isLoading}
            />
          )}

          {!currentStepConfig.hideNextButton && (
            <Button
              content={
                isLastStep
                  ? currentStepConfig.nextButtonText || 'Complete'
                  : currentStepConfig.nextButtonText || 'Next'
              }
              variant="primary"
              onClick={next}
              disabled={!canGoNext || isLoading}
              isLoading={isLoading}
            />
          )}
        </DialogActions>
      </Dialog>
    </MultiStepContext.Provider>
  );
}
